import { ab } from './interface'
import {
  Experiment,
  TrialByPlan15535Variant,
  FuzzySearch15538Variant,
  IosExternalLinkPayment16218Variant,
  Streaks16464Variant,
  PartnerSimplifiedOnboarding17236Variant,
  SignupPageRedesign17237Variant,
} from './types'

import { storage } from '@/helpers/storage'
import { runTextUpdates } from '@/services/ab/13448_ab_experiment'
import Cookies from 'js-cookie'
import { platform } from '@/services/platform'
import { platformData } from '@/services/platform.capacitor'
import { getIosStorefrontLocation } from '@/services/capacitor.storefrontLocationPlugin'

/**
 * Initialize CloudFront experiments.
 *
 * Check A/B testing cookies set by CloudFront, send backend
 * requests and update frontend a/b data storage.
 * Pushes AB experiments to GTM data layer when done.
 */
export async function initAB(): Promise<void> {
  await newCopy13448Experiment(Experiment.new_copy_pdf_v2_13448, '/pdf')
  await aiGuideFlow13747()
  const { GTM } = await import('@/analytics/gtm')
  GTM.pushABExperimentsToDataLayer()
  await saveSummaryHero17223Variant()
}

/**
 * Feature flag to enable/disable Google Billing. Disabled by default.
 *
 * This is not a real A/B experiment, i.e. the experiment is not activated on
 * the backend.
 */
export function googleBillingEnabled(): boolean {
  return ab.experimentVariant(Experiment.google_billing_7517) === 'yes'
}

/**
 * Apply the text changes on the public pages
 *
 * @param {Experiment} experiment - The A/B experiment containing the text changes.
 * @param {string} allowedUrlExpression - A regular expression to match allowed URLs,
 * on which the text changes should be applied.
 */
export async function newCopy13448Experiment(
  experiment: Experiment,
  allowedUrlExpression: string,
): Promise<void> {
  const pathname = window.location.pathname
  const isTranslatedPage = pathname.includes(`/es${allowedUrlExpression}/`)

  if (!pathname.includes(allowedUrlExpression) || isTranslatedPage) {
    // Only start experiment on non-translated pdf pages
    return
  }

  let result = ab.experimentVariant(experiment)

  if (result === null) {
    result = await ab.start(experiment)
  }

  if (result === 'yes') {
    runTextUpdates(experiment)
  }
}

/**
 * Get the variant of the ai_label_13537 experiment.
 * Returns boolean:
 *   true - Show the ai label component
 *   false - Don't show the ai label component
 */
export function aiLabel13537ExperimentVariant(): boolean {
  return ab.experimentVariant(Experiment.ai_label_13537) === 'yes'
}

/**
 * Decide wheter or not to render the ai content label component in
 * the book preview and summary pages.
 *   true - Show the ai label component
 *   false - Don't show the ai label component
 */
export async function aiLabel13537Experiment(): Promise<boolean> {
  let result = ab.experimentVariant(Experiment.ai_label_13537)

  if (result === null) {
    result = await ab.start(Experiment.ai_label_13537)
  }

  return result === 'yes'
}

/*
 * Read experiment cookies set by CloudFront and transfer the experiment assignment to the local storage
 */
export async function aiGuideFlow13747(): Promise<void> {
  const cfVariant = Cookies.get('X-AB-AI-Guide-Flow-13747')
  const localVariant = ab.experimentVariant(Experiment.ai_guide_flow_13747)

  if (cfVariant && !localVariant) {
    await ab.start(Experiment.ai_guide_flow_13747, cfVariant)
  }
}

export async function trialByPlan15535Experiment(): Promise<TrialByPlan15535Variant> {
  // We have experiment variant in the local storage.
  let result = ab.experimentVariant(
    Experiment.exp_trial_by_plan_15535,
  ) as TrialByPlan15535Variant

  const isAndroid = await platform.isAndroid()

  // We don't start the experiment on Android
  if (isAndroid) {
    return null
  }

  // Not Android, start the experiment
  if (result === null) {
    result = (await ab.start(
      Experiment.exp_trial_by_plan_15535,
    )) as TrialByPlan15535Variant
  }

  return result
}

export function trialByPlan15535Variant(): string | null {
  return ab.experimentVariant(
    Experiment.exp_trial_by_plan_15535,
  ) as TrialByPlan15535Variant
}

/**
 * The Fuzzy Search experiment.
 *
 * no - Use the current "literal" search, control variant.
 * yes - Use the fuzzy search, treatment variant
 */
export async function fuzzySearch15538Experiment(): Promise<FuzzySearch15538Variant> {
  let result = ab.experimentVariant(Experiment.exp_fuzzy_search_15538)

  if (result === null) {
    result = await ab.start(Experiment.exp_fuzzy_search_15538)
  }

  return result as FuzzySearch15538Variant
}

/**
 * Get the variant for the exp_fuzzy_search_15538 experiment.
 */
export function fuzzySearch15538Variant(): FuzzySearch15538Variant {
  return ab.experimentVariant(
    Experiment.exp_fuzzy_search_15538,
  ) as FuzzySearch15538Variant
}

/**
 * Get the variant for the ios_external_link_payment_16218 experiment.
 */
export async function iosExternalLinkPayment16218Experiment(): Promise<IosExternalLinkPayment16218Variant> {
  await platformData.init()
  const appBuild = platformData.appBuild
  // We need to get the storefront location for the experiment
  // and it's not available in builds < 60.
  if (appBuild < 60) {
    return null
  }
  const storefrontLocation = await getIosStorefrontLocation()
  // External payments are allowed in the US storefront only.
  if (storefrontLocation !== 'USA') {
    return null
  }

  // Check variant in the local storage
  let result = ab.experimentVariant(
    Experiment.exp_ios_external_link_payment_16218,
  )

  // Start the experiment
  if (result === null) {
    result = await ab.start(Experiment.exp_ios_external_link_payment_16218)
  }

  return result as IosExternalLinkPayment16218Variant
}

/**
 * Get the variant for the ios_external_link_payment_16218 experiment.
 */
export function iosExternalLinkPayment16218Variant(): IosExternalLinkPayment16218Variant {
  return ab.experimentVariant(
    Experiment.exp_ios_external_link_payment_16218,
  ) as IosExternalLinkPayment16218Variant
}

/**
 * Start the streaks_16464 experiment.
 *
 * A - Control, current logic without Streaks.
 * B - Version with the Streaks feature.
 */
export async function streaks16464Experiment(): Promise<Streaks16464Variant> {
  let result = ab.experimentVariant(Experiment.exp_streaks_16464)

  if (result === null) {
    result = await ab.start(Experiment.exp_streaks_16464)
  }

  return result as Streaks16464Variant
}

/**
 * Get the variant for the streaks_16464 experiment.
 */
export function streaks16464Variant(): Streaks16464Variant {
  return ab.experimentVariant(
    Experiment.exp_streaks_16464,
  ) as Streaks16464Variant
}

/**
 * Start the exp_partner_simplified_onboarding_17236 experiment.
 *
 * A - Control, current logic with onboarding.
 * B - Skip the onboarding and redirect directly to the billing page.
 */
export async function partnerSimplifiedOnboarding17236(): Promise<PartnerSimplifiedOnboarding17236Variant> {
  let result = ab.experimentVariant(
    Experiment.exp_partner_simplified_onboarding_17236,
  )

  if (result === null) {
    result = await ab.start(Experiment.exp_partner_simplified_onboarding_17236)
  }

  return result as PartnerSimplifiedOnboarding17236Variant
}

/**
 * Start the hero optimization experiment in the backend if we assigned a variant locally.
 *
 * A - Control, current hero section.
 * B - Updated hero section.
 *
 * To prevent delaying rendering the page, we assign the variant for this experiment in the
 * document's `<head>` in `index.v2.html`, and send the result to the backend through this
 * function when it's done loading.
 */
export async function summaryHeroOptimization17223(): Promise<void> {
  const newVariant = localStorage.getItem('variant_to_set_17223')
  const localVariant = ab.experimentVariant(
    Experiment.exp_summary_hero_optimization_17223,
  )

  if (newVariant && !localVariant) {
    await ab.start(Experiment.exp_summary_hero_optimization_17223, newVariant)
  }
}

/**
 * Start the hero optimization experiment in the backend if we assigned a variant locally.
 *
 * A - Control, current hero section.
 * B - Updated hero section.
 *
 * To prevent delaying rendering the page, we assign the variant for this experiment in the
 * document's `<head>` in `index.v2.html`, and send the result to the backend through this
 * function when it's done loading.
 */
export async function saveSummaryHero17223Variant(): Promise<void> {
  const newVariant = localStorage.getItem('variant_to_set_17223')
  const localVariant = ab.experimentVariant(
    Experiment.exp_summary_hero_optimization_17223,
  )

  if (newVariant && !localVariant) {
    await ab.start(Experiment.exp_summary_hero_optimization_17223, newVariant)
    localStorage.removeItem('variant_to_set_17223')
  }
}

/**
 * Start the exp_signup_page_redesign_17237 experiment if not yet set.
 * Called when the onboarding Signup screen mounts. Returns the variant (A, B, or null if excluded).
 * Partner traffic is excluded by sending variant _EXCLUDED_ to the backend. We check both
 * sf_first_page and sf_marketing_url: the user may have landed on a non-UTM page first and
 * later followed a referral link (saved in sf_marketing_url by detectAndSaveMarketingUrl).
 */
export async function signupPageRedesign17237Experiment(): Promise<SignupPageRedesign17237Variant> {
  let result = ab.experimentVariant(Experiment.exp_signup_page_redesign_17237)

  if (result === null) {
    const firstPage = storage.getItem('sf_first_page') || ''
    const marketingUrl = storage.getItem('sf_marketing_url') || ''
    const isPartnerTraffic =
      firstPage.includes('utm_medium=partner') ||
      marketingUrl.includes('utm_medium=partner')
    const variantToSend = isPartnerTraffic ? '_EXCLUDED_' : undefined
    result = await ab.start(
      Experiment.exp_signup_page_redesign_17237,
      variantToSend,
    )
  }

  return result as SignupPageRedesign17237Variant
}

/**
 * Get the variant for the exp_signup_page_redesign_17237 experiment.
 */
export function signupPageRedesign17237Variant(): SignupPageRedesign17237Variant {
  return ab.experimentVariant(
    Experiment.exp_signup_page_redesign_17237,
  ) as SignupPageRedesign17237Variant
}
